import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@aiwyn/auth';
import { Logger } from '@aiwyn/shared';
import { ROUTE_TOKEN_CXP_TASKS, ROUTE_TOKEN_MAIN_BASE } from '../../../routing-constants';

/**
 * Silently sends the caller to the tasks list when a CRL read is refused for
 * lack of access (PP-3817) — the URL pointed at a request/item this caller
 * cannot see, so there is nothing to render and no toast to raise (standing
 * CRL rule: no snackbars on this surface).
 *
 * A secure-link guest is deliberately NOT redirected: /main/tasks is out of
 * bounds for a guest session (it would trip the out-of-bounds dialog), and the
 * secure-link shell already owns the guest error surface — a no-grants link is
 * refused at entity resolution, before the hosted CRL ever mounts. For the
 * rare guest that still hits a refused read, the page keeps its normal error
 * state inside the shell.
 */
@Injectable({ providedIn: 'root' })
export class CxpCrlNoAccessRedirectService {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly logger = inject(Logger);

  private readonly user = this.authService.getUserSignal(true);

  redirectToTasks(): void {
    if (this.user()?.isSecureLinkUser() === true) return;
    this.logger.info('[CxpCrlNoAccessRedirect] CRL read refused for this caller — redirecting to the tasks list.');
    this.router.navigate([ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_CXP_TASKS]).catch((error) => {
      this.logger.error('[CxpCrlNoAccessRedirect] tasks redirect failed:', error);
    });
  }
}
